// Control payloads for POST control/{propertyId}, built the way the app's
// updateState does (app/screens/CommonSwichDetails.js). Cards, detail pages
// and schedules use these so every screen sends the same command.
import {
  DEVICE_TYPES,
  MODELS,
  ON_OFF_ACTION_MODELS,
  TRAITS,
  hasTrait,
  isModel,
  isType,
  modelOf,
} from "../constants/devices";
import { UserService } from "./UserService";
import { User } from "../storage/store";

const action = (device, trait, value, id) => ({
  on: [`${device.applianceId}`],
  off: [],
  action: { [device.applianceId]: { id, trait: Number(trait), value } },
});

const pin = (device) => Number(device.pin) || 1;

// Switches a device on or off. For sensors and the smart lock this arms and
// disarms them.
export const onOffPayload = (device, on) => {
  const value = on ? 1 : 0;
  if (isModel(device, MODELS.W1F6SM_1) && !hasTrait(device, TRAITS.ON_OFF)) {
    return action(device, TRAITS.FAN_SPEED, on ? Number(device.props?.speed) || 3 : 0, pin(device));
  }
  if (ON_OFF_ACTION_MODELS.includes(modelOf(device)) && hasTrait(device, TRAITS.ON_OFF)) {
    return action(device, TRAITS.ON_OFF, value, Number(device.pin));
  }
  if (isModel(device, MODELS.WPCS_1)) return action(device, TRAITS.ARM, value, 1);
  if (isType(device, DEVICE_TYPES.ROBOT_VACUUM_CLEANER) && hasTrait(device, TRAITS.VACUUM_POWER)) {
    return action(device, TRAITS.VACUUM_POWER, value, 1);
  }
  if (isType(device, DEVICE_TYPES.ENERGY_METER) && hasTrait(device, TRAITS.METER_RELAY)) {
    return action(device, TRAITS.METER_RELAY, value, 1);
  }
  const id = `${device.applianceId}`;
  if (on && isType(device, DEVICE_TYPES.FAN)) {
    return { on: [id], off: [], speeds: { [id]: Number(device.props?.speed) || 3 } };
  }
  return on ? { on: [id], off: [] } : { on: [], off: [id] };
};

// Sets a fan's speed (1-6), which also switches it on.
export const fanSpeedPayload = (device, speed) => {
  if (isModel(device, MODELS.W1F6SM_1)) return action(device, TRAITS.FAN_SPEED, speed, pin(device));
  const id = `${device.applianceId}`;
  return { on: [id], off: [], speeds: { [id]: speed } };
};

// Sets a dimmer's brightness (0-100).
export const dimmerPayload = (device, value) => action(device, TRAITS.DIMMER, value, pin(device));

// An RGB light's effect, colour and brightness, as the app's updateState
// sends them: an rgb entry next to on, so changing them also switches it on.
// effect is the animation key (mixed_constant, mixed_strobe, rgb_constant,
// rgb_fade, rgb_rotate); color is a hex value without # for the mixed effects
// and red, green or blue for the RGB ones; brightness, min and max are
// percentages (sent as 0-255); speed is the effect's step in milliseconds.
export const rgbPayload = (device, { effect, color, brightness, min, max, speed }) => {
  const id = `${device.applianceId}`;
  const level = (percent) => Math.round(255 * (percent / 100));
  const base = { device: modelOf(device), animation: effect };
  const settings = {
    mixed_constant: { ...base, color },
    mixed_strobe: { ...base, color, duration_ms: speed },
    rgb_constant: { ...base, color, intensity: level(brightness) },
    rgb_fade: { ...base, color, max_intensity: level(max), min_intensity: level(min), duration: speed },
    rgb_rotate: { ...base, intensity: level(brightness), duration: speed },
  }[effect];
  return settings ? { on: [id], off: [], rgb: { [id]: settings } } : null;
};

// Sends a command for the selected property.
export const sendControl = (payload) => UserService.stateUpdate(payload, User.getPropertyId());
